import { useCallback, useMemo } from 'react';
// Imports inside frontend/shared are extensionless on purpose. This file is
// bundled by Vite for the admin app *and* by Metro for the mobile app, and
// Metro's resolver cannot follow a '.js' specifier pointing at a '.ts' file —
// it fails the whole suite with "Cannot find module '../i18n/index.js'". The
// modules mobile already reaches (i18n/index, components/index) are written
// the same way.
import { useLocaleOrDefault } from '../i18n/index';
import { useDisplaySettings } from './useDisplaySettings';
import {
  formatBusinessDay,
  formatBusinessDayRange,
  formatInstantDay,
  formatTimestamp,
} from '../contracts/business-day';

/**
 * Write stored business days the way this tenant has decided they are written.
 *
 * `formatBusinessDay` is deliberately pure and takes the format, so it can be
 * tested and used outside React. Screens should not have to fetch the tenant
 * setting and pass it through every column definition, which is how a format
 * ends up being spelled out by hand in one screen and differently in the next
 * — so the tenant's format and the reader's locale are both bound here, once.
 *
 *   const { day, dayRange, at, atDay } = useBusinessDay();
 *   ...
 *   value: (w) => dayRange(w.validFromDate, w.validToDate)
 *   value: (w) => at(w.createdAt)
 *   value: (w) => atDay(w.certifiedAt)
 */
export function useBusinessDay(): {
  readonly day: (value: string | null | undefined) => string;
  readonly dayRange: (
    from: string | null | undefined,
    to: string | null | undefined,
  ) => string;
  readonly at: (value: string | number | Date | null | undefined) => string;
  readonly atDay: (value: string | number | Date | null | undefined) => string;
} {
  const locale = useLocaleOrDefault();
  const { dateFormat: format } = useDisplaySettings();

  const day = useCallback(
    (value: string | null | undefined) =>
      formatBusinessDay(value, format, locale),
    [format, locale],
  );

  const dayRange = useCallback(
    (from: string | null | undefined, to: string | null | undefined) =>
      formatBusinessDayRange(from, to, format, locale),
    [format, locale],
  );

  const at = useCallback(
    (value: string | number | Date | null | undefined) =>
      formatTimestamp(value, format, locale),
    [format, locale],
  );

  const atDay = useCallback(
    (value: string | number | Date | null | undefined) =>
      formatInstantDay(value, format, locale),
    [format, locale],
  );

  return useMemo(
    () => ({ day, dayRange, at, atDay }),
    [day, dayRange, at, atDay],
  );
}
